import { useCallback } from 'react'

interface UseAutocompleteKeyboardProps {
  suggestions: string[]
  activeSuggestionIndex: number
  setActiveSuggestionIndex: (index: number) => void
  onSelectSuggestion: (suggestion: string) => void
  onReset: () => void
  fieldName: string
}

/**
 * Hook for handling keyboard navigation in autocomplete components.
 */
export function useAutocompleteKeyboard({
  suggestions,
  activeSuggestionIndex,
  setActiveSuggestionIndex,
  onSelectSuggestion,
  onReset,
  fieldName
}: UseAutocompleteKeyboardProps) {
  const scrollToActiveItem = useCallback(
    (index: number) => {
      const element = document.getElementById(
        `${fieldName}-suggestion-${index}`
      )
      if (element) {
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        })
      }
    },
    [fieldName]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!suggestions.length) return

      let newIndex: number

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          newIndex =
            activeSuggestionIndex < suggestions.length - 1
              ? activeSuggestionIndex + 1
              : activeSuggestionIndex
          break

        case 'ArrowUp':
          e.preventDefault()
          newIndex = activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : 0
          break

        case 'Enter':
          e.preventDefault()
          if (
            activeSuggestionIndex >= 0 &&
            activeSuggestionIndex < suggestions.length
          ) {
            onSelectSuggestion(suggestions[activeSuggestionIndex])
          }
          return

        case 'Escape':
          e.preventDefault()
          onReset()
          return

        default:
          return
      }

      setActiveSuggestionIndex(newIndex)
      scrollToActiveItem(newIndex)
    },
    [
      suggestions,
      activeSuggestionIndex,
      onSelectSuggestion,
      onReset,
      setActiveSuggestionIndex,
      scrollToActiveItem
    ]
  )

  return { handleKeyDown }
}
